import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { formatCurrencyAmount, formatDateTime, getCurrencyLabel } from "@/lib/utils";
import { DEFAULT_LOCALE, resolveLocale } from "@/localization/constants";
import { translate } from "@/localization/translate";

const LocalizationContext = createContext(null);

function getBrowserLocale() {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  return resolveLocale(window.navigator.language);
}

export const LocalizationProvider = ({ children }) => {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const settings = useQuery(
    api.settings.getSettings,
    !isLoading && isAuthenticated ? {} : "skip",
  );
  const [locale, setLocaleState] = useState(getBrowserLocale);

  useEffect(() => {
    if (settings?.locale) {
      setLocaleState(resolveLocale(settings.locale));
    } else if (!isAuthenticated) {
      setLocaleState(getBrowserLocale());
    }
  }, [isAuthenticated, settings]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (nextLocale) => {
    setLocaleState(resolveLocale(nextLocale));
  };

  const value = useMemo(() => {
    return {
      locale,
      setLocale,
      t: (key, params = {}) => translate(locale, key, params),
      formatCurrency: (amount, currency) =>
        formatCurrencyAmount(amount, currency, locale),
      formatDateTime: (value, options) => formatDateTime(value, locale, options),
      getCurrencyLabel: (currency) => getCurrencyLabel(currency, locale),
    };
  }, [locale]);

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);

  if (!context) {
    throw new Error("useLocalization must be used within LocalizationProvider");
  }

  return context;
};
